/**
 * Store для настроек камеры QR сканера
 * Сохраняет настройки фокуса, экспозиции, яркости, выбор камеры
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface CameraSettings {
  // Выбор камеры: 'user' (фронтальная) или 'environment' (задняя)
  preferredCamera: 'user' | 'environment'
  
  // Режим фокуса
  focusMode: 'manual' | 'continuous' | 'single-shot'
  focusDistance: number // 0-1, для manual mode
  
  // Экспозиция
  exposureMode: 'manual' | 'continuous' | 'none'
  exposureCompensation: number // -3 to 3
  
  // Яркость/контрастность
  brightness: number // 0-255
  contrast: number // 0-255
  
  // Дополнительные фильтры для борьбы с засветами
  saturation: number // 0-200 (100 = норма, 0 = grayscale)
  grayscale: boolean // Включить черно-белый режим
  invert: boolean // Инверсия цветов
  threshold: number // 0-100, порог бинаризации (0 = выключено)
  
  // Чувствительность детектора движения (для автоактивации сканера)
  motionSensitivity: number // 0-255, порог чувствительности движения (выше = менее чувствительно)
  
  // Производительность
  maxScansPerSecond: number // 2-10
  videoResolution: 'low' | 'medium' | 'high'
  
  // Zoom
  zoom: number // 1-3
}

export const DEFAULT_CAMERA_SETTINGS: CameraSettings = {
  preferredCamera: 'user', // Фронтальная камера по умолчанию
  focusMode: 'continuous',
  focusDistance: 0.5,
  exposureMode: 'continuous',
  exposureCompensation: 0,
  brightness: 80, // Понижено для борьбы с засветами
  contrast: 150, // Повышено для резкости QR
  saturation: 50, // Понижено для уменьшения засветов
  grayscale: false,
  invert: false,
  threshold: 0, // Выключено по умолчанию
  motionSensitivity: 60, // Текущее значение (0-255, выше = менее чувствительно)
  maxScansPerSecond: 10, // Увеличено для лучшей реакции на QR-коды
  videoResolution: 'medium',
  zoom: 1,
}

interface CameraStore {
  settings: CameraSettings
  
  // Обновление настроек
  updateSettings: (partial: Partial<CameraSettings>) => void
  resetSettings: () => void
  
  // Получение MediaTrackConstraints для применения настроек
  getVideoConstraints: () => MediaTrackConstraints
}

export const useCameraStore = create<CameraStore>()(
  persist(
    (set, get) => ({
      settings: DEFAULT_CAMERA_SETTINGS,
      
      updateSettings: (partial) => {
        set((state) => ({
          settings: { ...state.settings, ...partial },
        }))
        console.log('[CameraStore] Settings updated:', partial)
      },
      
      resetSettings: () => {
        set({ settings: DEFAULT_CAMERA_SETTINGS })
        console.log('[CameraStore] Settings reset to defaults')
      },
      
      getVideoConstraints: () => {
        const settings = get().settings
        
        // Определяем разрешение
        let width: number, height: number
        switch (settings.videoResolution) {
          case 'low':
            width = 640
            height = 480
            break
          case 'medium':
            width = 1280
            height = 720
            break
          case 'high':
            width = 1920
            height = 1080
            break
        }
        
        // Формируем constraints
        const constraints: MediaTrackConstraints = {
          facingMode: settings.preferredCamera === 'environment' ? 'environment' : 'user',
          width: { ideal: width },
          height: { ideal: height },
        }
        
        // Добавляем расширенные constraints (если поддерживаются)
        // Эти настройки могут не поддерживаться на всех устройствах
        // Используем any для обхода TypeScript ограничений
        const advanced: any[] = []
        
        // Фокус
        if (settings.focusMode !== 'continuous') {
          advanced.push({
            focusMode: settings.focusMode,
          })
        }
        
        if (settings.focusMode === 'manual' && settings.focusDistance > 0) {
          advanced.push({
            focusDistance: settings.focusDistance,
          })
        }
        
        // Экспозиция
        if (settings.exposureMode !== 'continuous') {
          advanced.push({
            exposureMode: settings.exposureMode,
          })
        }
        
        if (settings.exposureCompensation !== 0) {
          advanced.push({
            exposureCompensation: settings.exposureCompensation,
          })
        }
        
        // Яркость и контрастность
        if (settings.brightness !== 128) {
          advanced.push({
            brightness: settings.brightness,
          })
        }
        
        if (settings.contrast !== 128) {
          advanced.push({
            contrast: settings.contrast,
          })
        }
        
        // Zoom
        if (settings.zoom > 1) {
          advanced.push({
            zoom: settings.zoom,
          })
        }
        
        if (advanced.length > 0) {
          (constraints as any).advanced = advanced
        }
        
        console.log('[CameraStore] Generated video constraints:', constraints)
        
        return constraints
      },
    }),
    {
      name: 'camera-settings-storage',
      version: 1,
    }
  )
)

